import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../store/toast/toastSlice";
import { useSocket } from "../context/SocketContext"; // Assuming SocketContext is already set up
import useAuth from "./useAuth"; // Assuming you have a custom hook for auth (to get user details)
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchGroupChatMessagesByChatroom,
  fetchGroupChatUsersForWorkOrder,
} from "../services/api/message";
import { useNotificationsSocket } from "./useNotificationsSocket";

const MESSAGES_PAGE_SIZE = 10;

// Marks one message read by `readerUserId`. The status row may legitimately not
// exist yet: the server broadcasts receiveMessage BEFORE it creates the
// per-recipient rows, so a message sent moments ago carries none.
const withReadStatus = (msg, readerUserId, user) => {
  const statuses = msg.chatMessageReadStatus || [];
  const existing = statuses.find((s) => s.userId === readerUserId);
  if (existing?.isRead) return msg;
  return {
    ...msg,
    chatMessageReadStatus: existing
      ? statuses.map((s) =>
          s.userId === readerUserId ? { ...s, isRead: true, user: user ?? s.user } : s,
        )
      : [...statuses, { userId: readerUserId, isRead: true, user }],
  };
};

export function useChatSocket(workOrderId, companyId, engineerId) {
  const { refetchMessageNotifications } = useNotificationsSocket();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [unreadCount, setUnreadCounts] = useState({});
  const { auth, authRole } = useAuth();
  const socket = useSocket();
  const workOrderClientId = useSelector((state) => state.workOrder.workOrderDetails?.createdBy?.id);
  const isCompanyOwner =
    authRole === "FREELANCER" && auth?.user?.company?.ownerId === auth?.user?.id;
  const [chatRoom, setChatRoom] = useState(
    () => queryClient.getQueryData(["activeChatRoom", workOrderId, engineerId]) || null,
  );
  const [chatUsersList, setChatUsersList] = useState([]);
  const [messages, setMessages] = useState([]);
  const [lastUnreadMessageId, setLastUnreadMessageId] = useState(null);
  // Whether the open room still accepts messages. Only the work order chat ever
  // closes (when the work is marked done); everything else stays open.
  const [chatRoomSendState, setChatRoomSendState] = useState({
    isDisabled: false,
    disabledMessage: null,
  });
  const lastJoinRef = useRef(null);

  const prevWorkOrderIdRef = useRef(workOrderId);
  useEffect(() => {
    if (prevWorkOrderIdRef.current === workOrderId) return;
    prevWorkOrderIdRef.current = workOrderId;
    lastJoinRef.current = null;
    setChatRoom(null);
    setMessages([]);
    setLastUnreadMessageId(null);
    setUnreadCounts({});
    setChatRoomSendState({ isDisabled: false, disabledMessage: null });
  }, [workOrderId]);

  const {
    isPending: isfetchGroupChatUsersForWorkOrderApiLoading,
    refetch: refetchChatUsers,
    data: chatUsersData,
  } = useQuery({
    queryKey: ["chatUsers", workOrderId, companyId],
    queryFn: () => fetchGroupChatUsersForWorkOrder({ workOrderId, companyId }),
    // Engineers and customers used to have exactly one room and so needed no
    // list. The work order chat gives them a second one, so they list too.
    enabled: !!workOrderId && !!companyId,
  });

  // History is paginated newest-page-first; each "next page" is an older slice that
  // the chat view prepends as the user scrolls up.
  const {
    isPending: isfetchGroupChatMessagesByChatroomApiLoading,
    data: messagesData,
    fetchNextPage: fetchOlderMessages,
    hasNextPage: hasMoreMessages,
    isFetchingNextPage: isFetchingOlderMessages,
  } = useInfiniteQuery({
    queryKey: ["chatRoomMessages", chatRoom?.id],
    queryFn: ({ pageParam }) =>
      fetchGroupChatMessagesByChatroom(chatRoom?.id, {
        cursor: pageParam,
        limit: MESSAGES_PAGE_SIZE,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.data?.nextCursor ?? undefined,
    enabled: !!chatRoom?.id,
    // Any refetch of an infinite query re-requests EVERY loaded page, so a user who
    // has scrolled back pays for it each time. The socket keeps the open room live,
    // so don't refetch on focus; the stale window only covers remounting the tab.
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  // Join group chat on mount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.off("joinSuccess");
        socket.off("newParticipant");
        handleLeaveGroupChat();
      }
    };
  }, [socket]);

  const handleMessageReadByUser = (data) => {
    const { messageId, readerUserId, conversationId, user } = data;

    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === messageId ? withReadStatus(msg, readerUserId, user) : msg,
      ),
    );
    // The cached pages are what `messages` is rebuilt from on the next cache
    // change - sending a message appends there - so the flag has to land in the
    // cache too, or every message already seen reverts to unseen.
    mapCachedMessages(conversationId, (msg) =>
      msg.id === messageId ? withReadStatus(msg, readerUserId, user) : msg,
    );
  };

  /* const messageDeliveredHandler = (data) => {
    // Log the delivered message
    console.log(`Message delivered to chat`, data);
    const { messageId, userId, conversationId } = data;

    setMessages((prevMessages) => {
      return prevMessages.map((msg) => {
        if (msg.id === messageId) {
          // Find the user who read the message and update the read status
          const chatMessageReadStatus = msg.chatMessageReadStatus || [];
          const existingStatusIndex = chatMessageReadStatus.findIndex(
            (status) => status.userId === userId
          );

          if (existingStatusIndex === -1) {
            chatMessageReadStatus.push({ userId: userId, isDelivered: false });
          } else {
            chatMessageReadStatus[existingStatusIndex] = {
              userId: userId,
              isDelivered: true,
            };
          }

          return { ...msg, chatMessageReadStatus };
        }
        return msg;
      });
    });
  }; */

  useEffect(() => {
    // console.log("socket", socket);
    if (!socket) return;

    // socket.on("messageDelivered", messageDeliveredHandler);
    socket.on("messageReadByUser", handleMessageReadByUser);

    const handleUnreadCountUpdated = (data) => {
      updateChatUsersRecentMessage(data);
      setUnreadCounts((prevCounts) => ({
        ...prevCounts,
        [data.conversationId]: data.unreadCount,
      }));
    };

    socket.on("unreadCountUpdated", handleUnreadCountUpdated);

    return () => {
      if (socket) {
        // socket.off("messageDelivered", messageDeliveredHandler);
        socket.off("messageReadByUser", handleMessageReadByUser);
        // Remove only OUR handler. `off(event)` with no reference detaches every
        // listener for that event — which silently killed the top-bar inbox's
        // subscription whenever the chat unmounted, freezing its badge.
        socket.off("unreadCountUpdated", handleUnreadCountUpdated);
      }
    };
  }, [socket]);

  const updateChatUsersRecentMessage = (data) => {
    // unreadCountUpdated is also emitted on join, where it carries only the
    // zeroed count and no message. Overwriting then wiped the row's preview, so
    // the text under a name vanished every time that chat was opened.
    if (!data?.recentMessage) return;

    setChatUsersList((prevUsers) => {
      return prevUsers.map((user) => {
        if (user.conversationId === data.conversationId) {
          return {
            ...user,
            recentMessage: data.recentMessage,
          };
        }
        return user;
      });
    });
  };

  // A message edited or deleted after it landed is still the row's preview, so
  // the list has to follow it. Only when it IS that row's latest — editing an
  // older message changes nothing the list shows.
  const patchChatUserRecentMessage = (conversationId, messageId, patch) => {
    if (!conversationId || !messageId) return;
    setChatUsersList((prevUsers) =>
      prevUsers.map((user) =>
        user.conversationId === conversationId && user.recentMessage?.id === messageId
          ? { ...user, recentMessage: { ...user.recentMessage, ...patch } }
          : user,
      ),
    );
  };

  const moveChatUserToTop = (data) => {
    // Same guard as above: never replace a row's preview with nothing.
    if (!data?.recentMessage) return;

    setChatUsersList((prevUsers) => {
      const idx = prevUsers.findIndex((user) => user.conversationId === data.conversationId);
      if (idx === -1) return prevUsers;
      const updated = {
        ...prevUsers[idx],
        recentMessage: data.recentMessage,
      };
      return [updated, ...prevUsers.slice(0, idx), ...prevUsers.slice(idx + 1)];
    });
  };

  // Socket updates have to land in the CACHED pages too, not only in `messages`.
  // `messages` holds one room at a time and is replaced on every thread switch,
  // so coming back re-renders from this cache - and inside its 60s stale window
  // nothing refetches. That is how a message sent in the work order chat was
  // gone on returning to it, until a reload fetched the room again.
  const patchCachedMessagePages = useCallback(
    (conversationId, updatePages) => {
      if (!conversationId) return;
      queryClient.setQueryData(["chatRoomMessages", conversationId], (cached) => {
        if (!cached?.pages?.length) return cached;
        return { ...cached, pages: updatePages(cached.pages) };
      });
    },
    [queryClient],
  );

  const appendCachedMessage = (conversationId, newMessage) =>
    patchCachedMessagePages(conversationId, (pages) => {
      const known = pages.some((page) =>
        (page?.data?.messages ?? []).some((m) => m.id === newMessage.id),
      );
      if (known) return pages;
      // Page 0 is the newest slice and its messages ascend, so the newest
      // message belongs at its end.
      return pages.map((page, index) =>
        index === 0
          ? {
              ...page,
              data: { ...page.data, messages: [...(page?.data?.messages ?? []), newMessage] },
            }
          : page,
      );
    });

  const mapCachedMessages = (conversationId, mapMessage) =>
    patchCachedMessagePages(conversationId, (pages) =>
      pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          messages: (page?.data?.messages ?? []).map(mapMessage),
        },
      })),
    );

  const patchCachedMessage = (conversationId, messageId, patch) =>
    patchCachedMessagePages(conversationId, (pages) =>
      pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          messages: (page?.data?.messages ?? []).map((m) =>
            m.id === messageId ? { ...m, ...patch } : m,
          ),
        },
      })),
    );

  useEffect(() => {
    if (!socket) return;

    socket.on("receiveMessage", (newMessage) => {
      moveChatUserToTop({
        conversationId: newMessage.conversationId,
        recentMessage: {
          message: newMessage.message,
          createdAt: newMessage.createdAt,
          messageType: newMessage.messageType ?? null,
          senderId: newMessage.senderId ?? newMessage.sender?.id ?? null,
          sender: newMessage.sender ?? null,
        },
      });
      // Every room the user is in, not just the open one: switching threads and
      // back must not lose what arrived while they were away.
      appendCachedMessage(newMessage.conversationId, newMessage);
      if (newMessage.conversationId !== chatRoom?.id) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === newMessage.id)) return prev;
        return [...prev, newMessage];
      });
    });

    // The server flips every existing read-status row for this reader, so mirror that
    // locally. Refetching instead would re-request every page loaded so far, and the
    // event is broadcast for any room the user is in — not just the open one.
    socket.on("allMessagesReadByUser", (data) => {
      if (!chatRoom?.id || data.chatRoomId !== chatRoom.id) return;
      setLastUnreadMessageId(data.lastUnreadMessageId);
      // They can't "read" their own message.
      const markRead = (m) =>
        m.senderId === data.readerUserId ? m : withReadStatus(m, data.readerUserId, data.user);
      setMessages((prev) => prev.map(markRead));
      // Same as the single-message event: the cache backs `messages`, so the
      // flags have to be written there as well to survive the next new message.
      mapCachedMessages(data.chatRoomId, markRead);
    });

    // Both events name their room, so they are applied to it rather than to
    // whichever room happens to be open — they reach every member, including
    // one reading a different thread at the time.
    socket.on("messageEdited", ({ conversationId, messageId, message, isEdited, editedAt }) => {
      const roomId = conversationId ?? chatRoom?.id;
      patchCachedMessage(roomId, messageId, { message, isEdited, editedAt });
      patchChatUserRecentMessage(roomId, messageId, { message });
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, message, isEdited, editedAt } : m)),
      );
    });

    socket.on("messageDeleted", ({ conversationId, messageId }) => {
      const roomId = conversationId ?? chatRoom?.id;
      patchCachedMessage(roomId, messageId, { isDeleted: true });
      patchChatUserRecentMessage(roomId, messageId, { isDeleted: true });
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, isDeleted: true } : m)));
    });

    socket.on("reactionUpdated", ({ conversationId, messageId, reactions }) => {
      patchCachedMessage(conversationId ?? chatRoom?.id, messageId, { reactions });
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reactions } : m)));
    });

    // The work order chat closes the moment the work is marked done. Lock the
    // composer right away rather than letting the next send be rejected.
    socket.on("chatRoomSendStateChanged", (data) => {
      if (!chatRoom?.id || data?.conversationId !== chatRoom.id) return;
      setChatRoomSendState({
        isDisabled: !!data?.isDisabled,
        disabledMessage: data?.disabledMessage ?? null,
      });
    });

    socket.on("removedFromChatRoom", (data) => {
      const roomId = data?.conversationId ?? data?.chatRoomId;
      if (chatRoom?.id && roomId === chatRoom.id) {
        setChatRoom({});
        setMessages([]);
      }
      if (roomId) queryClient.removeQueries({ queryKey: ["chatRoomMessages", roomId] });
      refetchChatUsers();
    });

    socket.on("sendError", (data) => {
      if (data?.isDisabled) {
        setChatRoomSendState({
          isDisabled: true,
          disabledMessage: data?.disabledMessage ?? null,
        });
      }
      dispatch(
        showToast({
          message: data?.message || "You are not authorized to send messages in this chat.",
          type: "error",
        }),
      );
    });

    return () => {
      socket.off("receiveMessage");
      socket.off("allMessagesReadByUser");
      socket.off("messageEdited");
      socket.off("messageDeleted");
      socket.off("reactionUpdated");
      socket.off("chatRoomSendStateChanged");
      socket.off("removedFromChatRoom");
      socket.off("sendError");
    };
  }, [socket, chatRoom]);

  useEffect(() => {
    if (!chatUsersData) return;
    const initialUnreadCounts = chatUsersData.data.reduce((acc, user) => {
      acc[user.conversationId] = user.unreadCount;
      return acc;
    }, {});

    setUnreadCounts(initialUnreadCounts);
    setChatUsersList(chatUsersData?.data);
  }, [chatUsersData]);

  // Page 0 is the newest slice and each following page is older, so reverse the page
  // order to get one ascending list.
  const serverMessages = useMemo(
    () =>
      (messagesData?.pages ?? [])
        .slice()
        .reverse()
        .flatMap((page) => page?.data?.messages ?? []),
    [messagesData],
  );

  useEffect(() => {
    if (!messagesData) return;
    setMessages((prev) => {
      if (!prev.length) return serverMessages;

      // Only messages already known to belong to THIS conversation may be kept.
      // Matching on an undefined room id used to be true for every leftover
      // message, so switching threads carried the previous one's messages over.
      const currentRoomId =
        serverMessages[serverMessages.length - 1]?.conversationId ?? chatRoom?.id ?? null;
      if (!currentRoomId) return serverMessages;

      const serverIds = new Set(serverMessages.map((m) => m.id));
      // Messages that arrived over the socket after this page was fetched.
      const localOnly = prev.filter(
        (m) => !serverIds.has(m.id) && m.conversationId === currentRoomId,
      );
      if (!localOnly.length) return serverMessages;

      // Merge by time rather than appending: locally-held messages are not
      // necessarily newer than everything the server just returned.
      return [...serverMessages, ...localOnly].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );
    });
  }, [messagesData, serverMessages]);

  // `chatRoomId` opens one specific thread. The tech is the counterpart of both
  // their engineer room and the work order chat, so the counterpart's id alone no
  // longer says which one to open.
  // (declared above, with the work order reset that clears it)

  const handleJoinGroupChat = (workOrderId, companyId, engineerId, chatRoomId) => {
    if (socket) {
      handleLeaveGroupChat();
      lastJoinRef.current = { workOrderId, companyId, engineerId, chatRoomId };

      socket.emit("joinGroupChat", {
        workOrderId,
        companyId,
        engineerId,
        ...(chatRoomId ? { chatRoomId } : {}),
        //  clientId: auth.user.id,
        // The acting user is always the logged-in user. A freelancer must join as
        // THEMSELVES — not as workOrderClientId (the work order's creator), which for
        // a customer-raised order is the customer and wrongly pulled the customer into
        // the freelancer's chat (and caused auth failures).
        clientId:
          authRole === "CLIENT" || authRole === "CUSTOMER" || authRole === "FREELANCER"
            ? auth.user.id
            : workOrderClientId || auth.user.id,
        authUserId: auth.user.id,
      });

      // Detach the previous join listeners before attaching new ones. These are
      // fresh closures on every join, so without this they accumulate and a single
      // joinSuccess fires one refetch per past join — a request storm now that
      // every role joins repeatedly (two rooms) and refetches the chat list.
      socket.off("joinSuccess");
      socket.off("joinError");
      socket.off("newParticipant");

      // Listen for success message
      socket.on("joinSuccess", (successData) => {
        if (successData?.firstVisibleMessageId && successData?.id) {
          queryClient.removeQueries({ queryKey: ["chatRoomMessages", successData.id] });
          setMessages([]);
        }
        setChatRoom(successData);
        setChatRoomSendState({
          isDisabled: !!successData?.isDisabled,
          disabledMessage: successData?.disabledMessage ?? null,
        });
        queryClient.setQueryData(["activeChatRoom", workOrderId, engineerId], successData);
        setUnreadCounts((prevCounts) => ({
          ...prevCounts,
          [successData.id]: 0,
        }));
        refetchMessageNotifications();
        refetchChatUsers();
      });

      socket.on("joinError", (err) => {
        console.error("[joinError]", err);
      });

      // Listen for new participants joining
      socket.on("newParticipant", (data) => {});
    }
  };

  const handleLeaveGroupChat = () => {
    if (socket) {
      setChatRoom({});
      setMessages([]);
      // Emit the event to leave the room on the server
      socket.emit("leaveChatRoom", {
        // clientId: auth.user.id,
        clientId: authRole === "CLIENT" ? auth.user.id : workOrderClientId || auth.user.id,
        authUserId: auth.user.id,
      });
    }
  };

  /**
   * Tells the server whether the joined conversation is actually on screen.
   *
   * The socket must stay in the room to receive live messages, but the Messages
   * tab stays mounted while the user is on another tab. Without this the server
   * treats them as reading: it zeroes their unread count and skips their
   * message notification.
   */
  const chatVisibilityRef = useRef({ visible: false, conversationId: null });

  const setChatVisibility = useCallback(
    (visible, conversationId) => {
      chatVisibilityRef.current = { visible, conversationId: conversationId ?? null };
      socket?.emit("chatRoomVisibility", { visible, conversationId: conversationId ?? null });
    },
    [socket],
  );

  // This lives on the socket, so a reconnect wipes it. Re-send the last reported
  // value on connect — otherwise the user would silently stop counting as
  // reading the conversation they are looking at.
  useEffect(() => {
    if (!socket) return;

    const resend = () => socket.emit("chatRoomVisibility", chatVisibilityRef.current);

    socket.on("connect", resend);
    return () => socket.off("connect", resend);
  }, [socket]);

  // A customer has a single engineer-less room with the team. Join it on the socket
  // `connect` event (and immediately if already connected). Doing it on connect —
  // rather than a one-shot effect — survives StrictMode's setup/cleanup churn (which
  // removes the joinSuccess listener) and re-joins on reconnect.
  useEffect(() => {
    if (authRole !== "CUSTOMER" || !socket || !workOrderId) return;

    const joinCustomerRoom = () => {
      // A customer now has two threads (their team chat and the work order chat),
      // so a reconnect must restore whichever one they had open.
      const last = lastJoinRef.current;
      if (last?.chatRoomId) {
        handleJoinGroupChat(last.workOrderId, last.companyId, last.engineerId, last.chatRoomId);
        return;
      }
      handleJoinGroupChat(workOrderId, companyId, auth?.user?.id);
    };

    if (socket.connected) {
      joinCustomerRoom();
    }
    socket.on("connect", joinCustomerRoom);

    return () => {
      socket.off("connect", joinCustomerRoom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authRole, socket, workOrderId, companyId]);

  const handleSendMessage = async (
    message,
    conversationId,
    workOrderId,
    engineerId,
    digitalAssetIds = [],
    messageType = null,
    replyToId = null,
  ) => {
    try {
      socket.emit("sendMessage", {
        conversationId,
        senderId: auth.user.id,
        message,
        workOrderId,
        engineerId,
        digitalAssetIds,
        messageType,
        ...(replyToId ? { replyToId } : {}),
      });

      moveChatUserToTop({
        conversationId,
        recentMessage: { message, createdAt: new Date().toISOString() },
      });
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleEditMessage = (messageId, _conversationId, newContent) => {
    socket.emit("editMessage", { messageId, senderId: auth.user.id, newContent });
  };

  const handleDeleteMessage = (messageId, _conversationId) => {
    socket.emit("deleteMessage", { messageId, senderId: auth.user.id });
  };

  const handleToggleReaction = (messageId, conversationId, emoji) => {
    socket.emit("toggleReaction", { messageId, userId: auth.user.id, emoji, conversationId });
  };

  return {
    chatUsersList,
    isfetchGroupChatUsersForWorkOrderApiLoading,
    isMessagesLoading: !!chatRoom?.id && isfetchGroupChatMessagesByChatroomApiLoading,
    handleJoinGroupChat,
    setChatVisibility,
    handleSendMessage,
    handleEditMessage,
    handleDeleteMessage,
    handleToggleReaction,
    refetchChatUsers,
    unreadCount,
    messages,
    chatRoom,
    chatRoomSendState,
    lastUnreadMessageId,
    isCompanyOwner,
    // Scroll-up pagination
    loadOlderMessages: fetchOlderMessages,
    hasMoreMessages: !!hasMoreMessages,
    isFetchingOlderMessages,
  };
}
